'use client';

import { type ComponentProps } from 'react';
import {
    Root,
    Group,
    Value,
    Trigger,
    Icon,
    Content,
    Label,
    Item,
    Separator,
    ScrollUpButton,
    ScrollDownButton,
    Portal,
    Viewport,
    ItemIndicator,
    ItemText,
} from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, type LucideIcon } from 'lucide-react';

import { cn } from '@/util/utils';

/**
 * A select.
 *
 * <p>IT NEVER REPORTS AN EMPTY CHOICE, and that is a fix rather than a preference. Radix keeps a
 * hidden native `select` beside the real one so the control submits with a form, and syncs it in
 * an effect: when the value changes it sets `select.value` and dispatches a `change`, whose
 * handler is the very `onValueChange` the caller passed. If the matching `option` is not in the
 * DOM at that moment, the browser refuses the assignment and leaves `select.value` as `""`, so the
 * event that arrives says the user chose nothing.</p>
 *
 * <p>That happens whenever a value arrives with, or before, the options it names, which is every
 * form whose value comes from one fetch and whose options come from another. The wardrobe piece
 * editor showed it: on the first open after a page load, Category resolved to "Clothing" and was
 * overwritten by the echo a render later, so the field sat empty and the save answered "Required."
 * (ALS-216). The caller has no way to tell that echo from a person clearing the field, and Radix
 * forbids an item whose value is `""`, so an empty value cannot be a real choice here and is
 * dropped.</p>
 */
function Select({
    value,
    defaultValue,
    open,
    defaultOpen,
    dir,
    name,
    autoComplete,
    disabled,
    required,
    form,
    ...props
}: ComponentProps<typeof Root>) {
    return (
        <Root
            data-slot="select"
            value={value}
            defaultValue={defaultValue}
            open={open}
            defaultOpen={defaultOpen}
            dir={dir}
            name={name}
            autoComplete={autoComplete}
            disabled={disabled}
            required={required}
            form={form}
            {...props}
            // After the spread, so it wraps whatever the caller passed rather than being
            // overwritten by it.
            onValueChange={(next) => {
                if (next !== '') props.onValueChange?.(next);
            }}
        />
    );
}

function SelectGroup({ asChild, className, ref, ...props }: ComponentProps<typeof Group>) {
    return (
        <Group
            data-slot="select-group"
            asChild={asChild}
            className={className}
            ref={ref}
            {...props}
        />
    );
}

function SelectValue({
    placeholder,
    asChild,
    className,
    ref,
    ...props
}: ComponentProps<typeof Value>) {
    return (
        <Value
            data-slot="select-value"
            placeholder={placeholder}
            asChild={asChild}
            className={className}
            ref={ref}
            {...props}
        />
    );
}

interface SelectTriggerProps extends ComponentProps<typeof Trigger> {
    size?: SelectTriggerSize;
    icon?: LucideIcon;
}

enum SelectTriggerSize {
    SM = 'sm',
    DEFAULT = 'default',
}

function SelectTrigger({
    className,
    size = SelectTriggerSize.DEFAULT,
    icon,
    children,
    asChild,
    ref,
    ...props
}: SelectTriggerProps) {
    const LucideIcon = icon;
    return (
        <Trigger
            data-slot="select-trigger"
            data-size={size}
            className={cn(
                "border-input [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-outline aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:focus-visible:bg-input/50 dark:hover:bg-input/50 bg-background flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:font-normal data-[placeholder]:tracking-normal data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:flex *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:flex-1 *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 *:data-[slot=select-value]:overflow-hidden [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                'bg-card text-foreground focus-visible:border-ring aria-invalid:border-destructive disabled:bg-muted disabled:text-text-locked aria-invalid:focus-visible:border-destructive h-9 rounded-md px-3 shadow-xs focus-visible:shadow-[0_0_0_3px_var(--outline)] focus-visible:ring-0 disabled:opacity-100 aria-invalid:focus-visible:shadow-[0_0_0_3px_var(--outline-destructive)] data-[size=sm]:h-8 pointer-coarse:min-h-11 pointer-coarse:text-base',
                className,
            )}
            asChild={asChild}
            ref={ref}
            {...props}
        >
            {LucideIcon && <LucideIcon className="text-muted-foreground size-4" />}
            {children}
            <Icon asChild>
                <ChevronDownIcon className="size-4" />
            </Icon>
        </Trigger>
    );
}

function SelectContent({
    className,
    children,
    position = 'popper',
    align = 'start',
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    side,
    sideOffset,
    alignOffset,
    arrowPadding,
    avoidCollisions,
    collisionBoundary,
    collisionPadding,
    sticky,
    hideWhenDetached,
    updatePositionStrategy,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof Content>) {
    return (
        <Portal>
            <Content
                data-slot="select-content"
                className={cn(
                    'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-[min(392px,var(--radix-select-content-available-height))] max-w-[max(var(--radix-select-trigger-width),min(320px,calc(100vw-1.5rem)))] min-w-[max(8rem,var(--radix-select-trigger-width))] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md',
                    'border-border rounded-md border p-1 shadow-md',
                    className,
                )}
                position={position}
                align={align}
                onCloseAutoFocus={onCloseAutoFocus}
                onEscapeKeyDown={onEscapeKeyDown}
                onPointerDownOutside={onPointerDownOutside}
                side={side}
                sideOffset={sideOffset ?? 6}
                alignOffset={alignOffset}
                arrowPadding={arrowPadding}
                avoidCollisions={avoidCollisions}
                collisionBoundary={collisionBoundary}
                collisionPadding={collisionPadding}
                sticky={sticky}
                hideWhenDetached={hideWhenDetached}
                updatePositionStrategy={updatePositionStrategy}
                asChild={asChild}
                ref={ref}
                {...props}
            >
                <SelectScrollUpButton />
                <Viewport
                    className={cn(
                        position === 'popper' &&
                            'h-[var(--radix-select-trigger-height)] w-full scroll-my-1',
                    )}
                >
                    {children}
                </Viewport>
                <SelectScrollDownButton />
            </Content>
        </Portal>
    );
}

function SelectLabel({ asChild, className, ref, ...props }: ComponentProps<typeof Label>) {
    return (
        <Label
            data-slot="select-label"
            asChild={asChild}
            className={cn('text-label-sm text-muted-foreground px-2 pt-2 pb-1', className)}
            ref={ref}
            {...props}
        />
    );
}

interface SelectItemProps extends ComponentProps<typeof Item> {
    icon?: LucideIcon;
}

function SelectItem({
    value,
    disabled,
    textValue,
    icon,
    asChild,
    ref,
    className,
    children,
    ...props
}: SelectItemProps) {
    const LucideIcon = icon;
    return (
        <Item
            data-slot="select-item"
            value={value}
            disabled={disabled}
            textValue={textValue}
            asChild={asChild}
            ref={ref}
            className={cn(
                "hover:bg-accent [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_span]:flex-1 [&_span]:font-normal [&_span]:tracking-normal [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:min-w-0 *:[span]:last:items-center *:[span]:last:gap-2",
                'focus:bg-accent focus:text-accent-foreground cursor-pointer gap-2 rounded-sm px-2 py-1.5 pointer-coarse:min-h-11',
                className,
            )}
            {...props}
        >
            {/* The prototype's ui-select__tick: 14px on the left, its width reserved on every row
                so a label does not shift when the selection moves. A div, so the row's
                [&_span] rules leave it alone. */}
            <div
                data-slot="select-item-tick"
                className="flex size-3.5 shrink-0 items-center justify-center"
            >
                <ItemIndicator>
                    <CheckIcon className="size-3.5 text-current" />
                </ItemIndicator>
            </div>
            {LucideIcon && <LucideIcon className="text-muted-foreground size-4" />}
            <ItemText>{children}</ItemText>
        </Item>
    );
}

function SelectSeparator({ className, asChild, ref, ...props }: ComponentProps<typeof Separator>) {
    return (
        <Separator
            data-slot="select-separator"
            className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
            asChild={asChild}
            ref={ref}
            {...props}
        />
    );
}

function SelectScrollUpButton({
    className,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof ScrollUpButton>) {
    return (
        <ScrollUpButton
            data-slot="select-scroll-up-button"
            className={cn(
                'flex cursor-default items-center justify-center gap-2 rounded-xs py-1',
                className,
            )}
            asChild={asChild}
            ref={ref}
            {...props}
        >
            <ChevronUpIcon className="text-foreground size-3.75" />
        </ScrollUpButton>
    );
}

function SelectScrollDownButton({
    className,
    asChild,
    ref,
    ...props
}: ComponentProps<typeof ScrollDownButton>) {
    return (
        <ScrollDownButton
            data-slot="select-scroll-down-button"
            className={cn(
                'flex cursor-default items-center justify-center gap-2 rounded-xs py-1',
                className,
            )}
            asChild={asChild}
            ref={ref}
            {...props}
        >
            <ChevronDownIcon className="text-foreground size-3.75" />
        </ScrollDownButton>
    );
}

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
    SelectTriggerSize,
};
